import React from 'react';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { getTemplateConfig } from '../helpers/templateConfig';
import { useCurrentAiActivity } from '../helpers/useAiActivityApi';
import { ExternalLink, Terminal, Copy, CheckCircle2, FileCode2, Loader2 } from 'lucide-react';
import styles from './CodegenPreviewPanel.module.css';

interface CodegenRunInfo {
  commands: string[];
  onlinePlayground?: { name: string; url: string };
}

const CODEGEN_RUN_INSTRUCTIONS: Record<string, CodegenRunInfo> = {
  python: {
    commands: ['pip install -r requirements.txt', 'python app.py'],
    onlinePlayground: { name: 'PyScript Playground', url: 'https://pyscript.com/' },
  },
  flask: {
    commands: ['pip install -r requirements.txt', 'flask run'],
    onlinePlayground: { name: 'PyScript Playground', url: 'https://pyscript.com/' },
  },
  fastapi: {
    commands: ['pip install -r requirements.txt', 'uvicorn main:app --reload'],
    onlinePlayground: { name: 'PyScript Playground', url: 'https://pyscript.com/' },
  },
  django: {
    commands: [
      'pip install -r requirements.txt',
      'python manage.py migrate',
      'python manage.py runserver',
    ],
    onlinePlayground: { name: 'PyScript Playground', url: 'https://pyscript.com/' },
  },
  flutter: {
    commands: ['flutter pub get', 'flutter run'],
    onlinePlayground: { name: 'DartPad', url: 'https://dartpad.dev/' },
  },
  rust: {
    commands: ['cargo build', 'cargo run'],
    onlinePlayground: { name: 'Rust Playground', url: 'https://play.rust-lang.org/' },
  },
  go: {
    commands: ['go mod tidy', 'go run .'],
    onlinePlayground: { name: 'Go Playground', url: 'https://go.dev/play/' },
  },
  godot: {
    commands: ['Open the project in Godot Editor', 'Press F5 or click Play'],
  },
  ci: {
    commands: ['Push to GitHub to trigger workflows', 'Check Actions tab for results'],
  },
  laravel: {
    commands: ['composer install', 'php artisan key:generate', 'php artisan serve'],
  },
};

interface CodegenPreviewPanelProps {
  templateId: string;
  sessionId: string;
  files: Selectable<SessionFiles>[];
}

export const CodegenPreviewPanel: React.FC<CodegenPreviewPanelProps> = ({
  templateId,
  sessionId,
  files,
}) => {
  const config = getTemplateConfig(templateId);
  const runInfo = CODEGEN_RUN_INSTRUCTIONS[templateId];
  const { isActive, currentActivity } = useCurrentAiActivity(sessionId);

  const accentColor = config?.color || '#61dafb';
  const accentBg = config?.bgColor || 'rgba(97, 218, 251, 0.15)';
  const accentBorder = config?.borderColor || 'rgba(97, 218, 251, 0.3)';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const fileCount = files.length;
  const progress = currentActivity?.progress ?? 0;
  const currentStep = currentActivity?.currentStep;
  const isGenerating =
    isActive && (currentActivity?.status === 'pending' || currentActivity?.status === 'processing');

  // Group files by directory for the summary
  const filesByDir: Record<string, string[]> = {};
  for (const f of files) {
    const dir = f.filePath || '/';
    if (!filesByDir[dir]) filesByDir[dir] = [];
    filesByDir[dir].push(f.fileName);
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {/* Template badge */}
        <div
          className={styles.badge}
          style={{
            color: accentColor,
            backgroundColor: accentBg,
            borderColor: accentBorder,
          }}
        >
          <span className={styles.badgeIcon}>{config?.icon}</span>
          <span className={styles.badgeName}>{config?.name || templateId}</span>
        </div>

        {/* --- GENERATING STATE: Progress bar --- */}
        {isGenerating ? (
          <div className={styles.progressSection}>
            <div className={styles.progressHeader}>
              <Loader2 size={18} className={styles.spinner} style={{ color: accentColor }} />
              <h2 className={styles.heading}>Generating Code</h2>
            </div>
            {currentStep && <p className={styles.currentStep}>{currentStep}</p>}
            <div className={styles.progressBarOuter}>
              <div
                className={styles.progressBarInner}
                style={{
                  width: `${Math.max(progress, 5)}%`,
                  backgroundColor: accentColor,
                }}
              />
            </div>
            <p className={styles.progressLabel}>
              {progress > 0 ? `${Math.round(progress)}%` : 'Starting...'} — {fileCount} file
              {fileCount !== 1 ? 's' : ''} so far
            </p>
          </div>
        ) : (
          /* --- COMPLETE STATE: Summary + run instructions --- */
          <>
            {/* Summary header */}
            <div className={styles.summaryHeader}>
              <CheckCircle2 size={20} style={{ color: accentColor }} />
              <h2 className={styles.heading}>
                {fileCount > 0 ? 'Code Ready' : 'Code Generation Only'}
              </h2>
            </div>

            {/* File summary */}
            {fileCount > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <FileCode2 size={16} style={{ color: accentColor }} />
                  <h3 className={styles.sectionTitle}>
                    {fileCount} file{fileCount !== 1 ? 's' : ''} generated
                  </h3>
                </div>
                <div className={styles.fileSummary} style={{ borderColor: accentBorder }}>
                  {Object.entries(filesByDir).map(([dir, fileNames]) => (
                    <div key={dir} className={styles.dirGroup}>
                      <span className={styles.dirName}>{dir}</span>
                      <div className={styles.fileList}>
                        {fileNames.map((name) => (
                          <span key={name} className={styles.fileName}>
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How to Run section */}
            {runInfo && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Terminal size={16} style={{ color: accentColor }} />
                  <h3 className={styles.sectionTitle}>Run Locally</h3>
                </div>
                <div className={styles.commandsContainer} style={{ borderColor: accentBorder }}>
                  {runInfo.commands.map((cmd, i) => (
                    <div key={i} className={styles.commandRow}>
                      <code className={styles.commandCode}>{cmd}</code>
                      <button
                        className={styles.copyBtn}
                        onClick={() => copyToClipboard(cmd)}
                        title="Copy command"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Online Playground link */}
            {runInfo?.onlinePlayground && (
              <a
                href={runInfo.onlinePlayground.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.playgroundLink}
                style={{
                  color: accentColor,
                  borderColor: accentBorder,
                }}
              >
                <ExternalLink size={16} />
                <span>Try on {runInfo.onlinePlayground.name}</span>
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
};
